/**
 * Finds the nearest field that remains in the active review list after the
 * current field falls out of it.
 *
 * This preserves the user's relative position in the review flow instead of
 * jumping back to the first field whenever a suggestion is resolved.
 *
 * @param {Object} params
 * @param {string[]} params.previousFieldIds Ordered field IDs before the mutation.
 * @param {string[]} params.nextFieldIds Ordered field IDs after the mutation.
 * @param {string|null} params.currentFieldId Field that was active when the mutation started.
 * @returns {string|null}
 */
export const findNearestRemainingReviewField = ({
  previousFieldIds = [],
  nextFieldIds = [],
  currentFieldId = null,
}) => {
  if (!currentFieldId || nextFieldIds.length === 0) return null;
  if (nextFieldIds.includes(currentFieldId)) return currentFieldId;

  const currentIndex = previousFieldIds.indexOf(currentFieldId);
  if (currentIndex === -1) {
    return nextFieldIds[0] || null;
  }

  for (let index = currentIndex + 1; index < previousFieldIds.length; index += 1) {
    const candidate = previousFieldIds[index];
    if (nextFieldIds.includes(candidate)) {
      return candidate;
    }
  }

  for (let index = currentIndex - 1; index >= 0; index -= 1) {
    const candidate = previousFieldIds[index];
    if (nextFieldIds.includes(candidate)) {
      return candidate;
    }
  }

  return nextFieldIds[0] || null;
};

/**
 * Where the Suggestions panel should move after the list of fields with
 * suggestions changes, or null to stay put.
 *
 * It moves only when the field the user is on just LEFT the list (its last
 * pending suggestion was accepted, rejected or dismissed). A field that was
 * never in the list (one with no suggestions, opened from its own review
 * button) keeps the panel: before 2026-11-04 the panel jumped from it to the
 * first field with a pending suggestion, so on Brooke McNicholas's TMD DBQ a
 * suggestion started on Left Comments saved to Right Comments.
 *
 * @param {Object} params
 * @param {string[]} params.previousFieldIds Ordered field IDs before the change.
 * @param {string[]} params.nextFieldIds Ordered field IDs after the change.
 * @param {string|null} params.currentFieldId The field the panel is on.
 * @returns {string|null}
 */
export const reviewFieldToMoveTo = ({
  previousFieldIds = [],
  nextFieldIds = [],
  currentFieldId = null,
}) => {
  if (!currentFieldId || nextFieldIds.length === 0) return null;
  if (nextFieldIds.includes(currentFieldId)) return null;
  if (!previousFieldIds.includes(currentFieldId)) return null;
  const next = findNearestRemainingReviewField({ previousFieldIds, nextFieldIds, currentFieldId });
  return next && next !== currentFieldId ? next : null;
};

export default findNearestRemainingReviewField;
